import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { zql } from '../schema.js';
import { itemsDetail, statusItems } from '../util/event-log-items.js';
import { titleCase } from '../util/index.js';
import { assertCan } from '../utils.js';
import { DeleteResourceSchema, UpdateResourceStatusSchema, EventLogResources, addEventLog } from './util.js';
import { ConsumableCategory, CompoundMappingType } from '../database/schema.js';
import { upsertCompoundMapping } from './compounders/utils.js';
export const ConsumableSchema = z.object({
    name: z.string().trim().nonempty(),
    description: z.string(),
    barcode: z
        .string()
        .nullable()
        .transform(value => value || null),
    nationalId: z
        .string()
        .nullable()
        .transform(value => value || null),
    category: ConsumableCategory,
    unitsPerPackage: z.number(),
    minimumStock: z.number(),
    /**
     * Smart compounder machine master-data id. Keeps a `compound_mappings` row
     * (`sourceId` = this consumable's id) in sync for the IV_SET mapping type.
     * Empty clears the mapping.
     */
    machineId: z.string().trim().optional(),
});
export const AddConsumableSchema = ConsumableSchema;
export const UpdateConsumableSchema = ConsumableSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
export const UpdateConsumableNationalIdSchema = z.object({
    id: z.uuidv7(),
    nationalId: z.string(),
});
async function findById(tx, id) {
    return await tx.run(zql.consumables.where('id', '=', id).where('deletedAt', 'IS', null).one());
}
export const consumables = {
    add: defineMutator(AddConsumableSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const newConsumable = {
            id: uuidv7(),
            name: titleCase(args.name),
            description: args.description,
            barcode: args.barcode,
            nationalId: args.nationalId,
            category: args.category,
            unitsPerPackage: args.unitsPerPackage,
            minimumStock: args.minimumStock,
            ...userActions,
        };
        await tx.mutate.consumables.insert(newConsumable);
        await upsertCompoundMapping(tx, {
            mappingType: CompoundMappingType.enum.IV_SET,
            sourceId: newConsumable.id,
            machineId: args.machineId,
            actorId: ctx.user.id,
            now,
        });
        if (tx.location === 'server') {
            const patches = createPatch({}, newConsumable);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.consumable,
                resourceId: newConsumable.id,
                action: 'create',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: newConsumable.id,
                    name: newConsumable.name,
                },
            }, ctx);
        }
    }),
    update: defineMutator(UpdateConsumableSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not Found');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        const updatedConsumable = {
            id: args.id,
            name: titleCase(args.name),
            description: args.description,
            barcode: args.barcode,
            nationalId: args.nationalId,
            category: args.category,
            unitsPerPackage: args.unitsPerPackage,
            minimumStock: args.minimumStock,
            ...userActions,
        };
        await tx.mutate.consumables.update(updatedConsumable);
        await upsertCompoundMapping(tx, {
            mappingType: CompoundMappingType.enum.IV_SET,
            sourceId: args.id,
            machineId: args.machineId,
            actorId: ctx.user.id,
            now,
        });
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedConsumable);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.consumable,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: updatedConsumable.id,
                    name: updatedConsumable.name,
                },
            }, ctx);
        }
    }),
    updateNationalId: defineMutator(UpdateConsumableNationalIdSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedConsumable = {
            id: args.id,
            nationalId: args.nationalId,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.consumables.update(updatedConsumable);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedConsumable);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.consumable,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedConsumable.updatedBy,
                performedAt: updatedConsumable.updatedAt,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: updatedConsumable.id,
                    name: prev.name,
                },
            }, ctx);
        }
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const updatedConsumable = {
            id: args.id,
            status: args.status,
            updatedAt: Date.now(),
            updatedBy: ctx.user.id,
        };
        await tx.mutate.consumables.update(updatedConsumable);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedConsumable);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.consumable,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedConsumable.updatedBy,
                performedAt: updatedConsumable.updatedAt,
                status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                domain: 'configuration',
                details: {
                    id: updatedConsumable.id,
                    name: prev.name,
                    ...itemsDetail(statusItems(prev.status, args.status)),
                },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const userActions = { deletedAt: now, deletedBy: ctx.user.id };
        const updatedConsumable = {
            id: args.id,
            barcode: prev.barcode ? `${prev.barcode}_deleted_${now}` : null,
            ...userActions,
        };
        await tx.mutate.consumables.update(updatedConsumable);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedConsumable);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.consumable,
                resourceId: args.id,
                action: 'delete',
                patches: patches,
                performedBy: updatedConsumable.deletedBy,
                performedAt: updatedConsumable.deletedAt,
                status: 'WARNING',
                domain: 'configuration',
                details: {
                    id: updatedConsumable.id,
                    name: prev.name,
                },
            }, ctx);
        }
    }),
};
//# sourceMappingURL=consumables.mutators.js.map